
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Paper, Typography, IconButton, Avatar, InputBase,
    Divider, CircularProgress, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RemoveIcon from '@mui/icons-material/Remove';
import { useLineChat } from '../hooks/useLineChat';

interface LazyImageProps {
    src: string | null;
    alt: string;
    style?: React.CSSProperties;
    onClick?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, style, onClick }) => {
    const [loaded, setLoaded] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const imgRef = useRef<HTMLDivElement>(null);

    const loadImage = useCallback(() => {
        if (!src) return;
        const img = new Image();
        img.src = src;
        img.onload = () => {
            setLoaded(true);
            setError(false);
        };
        img.onerror = () => {
            setError(true);
        };
    }, [src]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !loaded) {
                    loadImage();
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.1 });

        if (imgRef.current) observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, [src, loaded, loadImage]);

    if (error) {
        return (
            <Box
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fee2e2',
                    color: '#b91c1c',
                    fontSize: '11px',
                    minHeight: '100px',
                    borderRadius: '12px'
                }}
            >
                画像の読み込みに失敗
            </Box>
        );
    }

    return (
        <div ref={imgRef} style={{ position: 'relative', minHeight: loaded ? 'auto' : '100px' }}>
            {!loaded && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#f1f5f9', borderRadius: '12px'
                }}>
                    <CircularProgress size={20} />
                </Box>
            )}
            <img
                src={loaded ? (src || '') : ''}
                alt={alt}
                style={{
                    ...style,
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    visibility: loaded ? 'visible' : 'hidden'
                }}
                onClick={onClick}
            />
        </div>
    );
};

interface StaffMiniWindowProps {
    user: any;
    onClose: () => void;
}

const StaffMiniWindow: React.FC<StaffMiniWindowProps> = ({ user, onClose }) => {
    const { messages, fetchMessages, sendMessage, markRead } = useLineChat();
    const [inputText, setInputText] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isMinimized, setIsMinimized] = useState<boolean>(false);
    const [isComposing, setIsComposing] = useState<boolean>(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const lineUserId: string = user.line_user_id;

    const chatMessages: any[] = messages[lineUserId] || [];
    const staffName: string = user.reservation_ids?.find((id: any) => String(id).startsWith('STAFF:'))?.replace('STAFF:', '') || user.line_display_name;

    const LINE_API_URL: string = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
    const API_KEY: string = 'nc-line-sync-key-2024';

    // Initial fetch and polling
    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            await fetchMessages(lineUserId);
            setIsLoading(false);
            markRead(lineUserId);
        };
        load();

        const timer = setInterval(() => {
            fetchMessages(lineUserId);
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(timer);
    }, [lineUserId, fetchMessages, markRead]);

    // Auto-scroll to bottom（ユーザーが上にスクロール中は抑制）
    const isUserScrollingRef = useRef(false);
    useEffect(() => {
        if (scrollRef.current && !isMinimized && !isUserScrollingRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [chatMessages, isMinimized]);

    // スクロール位置を監視し、底付近でなければユーザーが遡っていると判定
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            isUserScrollingRef.current = distFromBottom > 100;
        };
        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [isMinimized]);

    const handleSend = async () => {
        if (!inputText.trim()) return;
        const textToSend = inputText.trim();
        setInputText('');
        await sendMessage(lineUserId, textToSend);
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    if (isMinimized) {
        return (
            <Paper
                elevation={6}
                sx={{
                    width: 200,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    borderRadius: '12px 12px 0 0',
                    background: '#1e293b',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': { background: '#334155' }
                }}
                onClick={() => setIsMinimized(false)}
            >
                {user.unread_count > 0 && (
                    <Box sx={{
                        position: 'absolute',
                        top: -6,
                        left: -6,
                        background: '#ef4444',
                        color: 'white',
                        minWidth: 20,
                        height: 20,
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 800,
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}>
                        {user.unread_count}
                    </Box>
                )}
                <Avatar src={user.line_picture_url} sx={{ width: 28, height: 28, mr: 1 }} />
                <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                    {staffName}
                </Typography>
                <IconButton size="small" onClick={(e: any) => { e.stopPropagation(); onClose(); }} sx={{ color: 'white' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={12}
            sx={{
                width: 320,
                height: 440,
                display: 'flex',
                direction: 'ltr',
                flexDirection: 'column',
                borderRadius: '12px 12px 0 0',
                overflow: 'hidden',
                background: '#fff',
                border: '1px solid #e2e8f0'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    background: '#1e293b',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {user.unread_count > 0 && (
                    <Box sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        background: '#ef4444',
                        color: 'white',
                        minWidth: 20,
                        height: 20,
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 800,
                        border: '2px solid #1e293b',
                    }}>
                        {user.unread_count}
                    </Box>
                )}
                <Avatar src={user.line_picture_url} sx={{ width: 32, height: 32, mr: 1.5 }} />
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" fontWeight={800} noWrap>
                        {staffName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }} noWrap>
                        {user.line_display_name}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => setIsMinimized(true)} sx={{ color: 'white', ml: 0.5 }}>
                    <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onClose} sx={{ color: 'white', ml: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Messages Body */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    background: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                }}
            >
                {isLoading && chatMessages.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
                ) : (
                    chatMessages.map((msg: any, idx: number) => {
                        const isMe = msg.direction === 'outbound';
                        // Construct image URL like LineChatPanel
                        const imgSrc = msg.type === 'image'
                            ? (isMe && msg.content.startsWith('http')
                                ? msg.content
                                : `${LINE_API_URL}?action=get_message_content&message_id=${msg.line_message_id || msg.id}&api_key=${API_KEY}`)
                            : null;

                        return (
                            <Box
                                key={msg.id || idx}
                                sx={{
                                    alignSelf: isMe ? 'flex-end' : 'flex-start',
                                    maxWidth: '85%',
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: msg.type === 'image' ? 0.5 : 1.2,
                                        px: msg.type === 'image' ? 0.5 : 1.5,
                                        borderRadius: isMe ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                                        background: isMe ? '#4f46e5' : '#fff',
                                        color: isMe ? '#fff' : '#1e293b',
                                        border: isMe ? 'none' : '1px solid #e2e8f0',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {msg.type === 'image' ? (
                                        <LazyImage
                                            src={imgSrc}
                                            alt="image"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '240px',
                                                display: 'block',
                                                borderRadius: '12px',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => window.open(imgSrc!, '_blank')}
                                        />
                                    ) : (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                                            {msg.content}
                                        </Typography>
                                    )}
                                </Paper>
                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', px: 0.5, opacity: 0.5, textAlign: isMe ? 'right' : 'left' }}>
                                    {new Date(msg.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* Input Area */}
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1, background: '#fff', borderTop: '1px solid #f1f5f9' }}>
                <InputBase
                    placeholder="メッセージを入力..."
                    multiline
                    maxRows={3}
                    value={inputText}
                    onChange={(e: any) => setInputText(e.target.value)}
                    onCompositionStart={() => setIsComposing(true)}
                    onCompositionEnd={() => setIsComposing(false)}
                    onKeyDown={(e: any) => {
                        // 日本語変換確定時のEnterキー(isComposing)では送信しない
                        if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    sx={{
                        flex: 1,
                        fontSize: '14px',
                        background: '#f1f5f9',
                        borderRadius: '20px',
                        px: 2,
                        py: 0.8
                    }}
                />
                <IconButton
                    disabled={!inputText.trim()}
                    onClick={handleSend}
                    sx={{
                        color: inputText.trim() ? '#4f46e5' : '#cbd5e1',
                        transition: 'all 0.2s'
                    }}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>
        </Paper>
    );
};

export default StaffMiniWindow;
